import React, { useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { checkProfileCompletion, api, API_BASE_URL, isGuestUser } from '../../services/api';
import { Button, SvgIcon } from '@mui/material';
import Dialog from '../dialogs/Dialog';
import { glassButtonSx } from './oauthButtonStyles';

// Google公式の4色「G」ロゴ（単色化・改変はブランド規約違反のため公式配色を使用）
const GoogleColorIcon = (props: any) => (
  <SvgIcon {...props} viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
  </SvgIcon>
);

interface GoogleLoginButtonProps {
  onLoginSuccess: (user: any) => void;
  redirectPath: string;
}

const GoogleLoginButton: React.FC<GoogleLoginButtonProps> = ({ onLoginSuccess, redirectPath }) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const login = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        // Googleユーザー情報を取得
        const userInfoResponse = await axios.get(
          'https://www.googleapis.com/oauth2/v3/userinfo',
          {
            headers: {
              Authorization: `Bearer ${tokenResponse.access_token}`,
            },
          }
        );

        // バックエンドに送信
        // ゲスト利用中のみapiインスタンス（JWT自動付与＋期限切れ時の自動refresh）で呼び、
        // ゲスト→本登録の昇格（同一User行への紐付け）を行う。
        // 非ゲストは素のaxios（ヘッダなし）。localStorageに残った期限切れトークンが
        // 付与されてログイン自体が401になる退行を防ぐため。
        const payload = {
          access_token: tokenResponse.access_token,
          user_info: userInfoResponse.data
        };
        const response = isGuestUser()
          ? await api.post(`/auth/google/login/token/`, payload)
          : await axios.post(`${API_BASE_URL}/auth/google/login/token/`, payload);
        
        // トークンを保存
        localStorage.setItem('token', response.data.access_token);
        localStorage.setItem('refreshToken', response.data.refresh_token);
        
        // ユーザー情報を保存
        localStorage.setItem('user', JSON.stringify(response.data.user));

        // プロフィール完成度チェック
        const isProfileComplete = await checkProfileCompletion();
        
        // 完成度に基づいて遷移
        if (!isProfileComplete) {
          navigate('/profile/settings', { replace: true });
        } else {
          navigate(redirectPath, { replace: true });
        }

        // ヘッダーの状態を更新
        onLoginSuccess(response.data.user);
      } catch (error: any) {
        console.error('Google login failed', error);
        // ゲストが既存アカウントでログインした場合は GuestModeUI が専用ダイアログを出すため、
        // ここではエラーダイアログを重ねて出さない
        if (error.response?.data?.code === 'guest_existing_account') return;
        setError(error.response?.data?.error || 'ログインに失敗しました');
      }
    },
    onError: () => {
      setError('ログインに失敗しました');
    }
  });

  const handleCloseError = () => {
    setError(null);
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={() => login()}
        startIcon={<GoogleColorIcon />}
        fullWidth
        sx={[glassButtonSx, { mt: 2 }]}
      >
        Googleでログイン
      </Button>
      
      <Dialog
        open={!!error}
        onClose={handleCloseError}
        title="エラー"
        content={error || ''}
      />
    </>
  );
};

export default GoogleLoginButton;